import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router-dom";
import messages from "./messages.i18n";
import { PeekBox, PeekPaper, useTheme } from "@piiqtechnologies/ui-components";
import { RefetchProvider, useAnalytics, useCommunity, useError, useLoader, CASA_FEATURES, usePolicyContext, } from "@piiqtechnologies/ui-lib";
import TabsLayout from "@layouts/tabs";
import { PATHS } from "@constants/Paths";
import { useAuthContext } from "@stores/AuthContext";
const CASA_FEATURES_SET = new Set([CASA_FEATURES.VT_CASA, CASA_FEATURES.SGT_CASA, CASA_FEATURES.WIDGET_CASA]);
export const CommunitySettingsLayout = () => {
    const intl = useIntl();
    const theme = useTheme();
    const { id } = useParams();
    const { setError } = useError();
    const analytics = useAnalytics();
    const { setLoader } = useLoader();
    const { proxyUser } = useAuthContext();
    const pageCtx = useOutletContext();
    const [getCommunity, community, communityLoading, communityError] = useCommunity();
    useEffect(() => {
        getCommunity({ id: id });
    }, []);
    useEffect(() => {
        setLoader(communityLoading, "community_details");
        return () => setLoader(false, "community_details");
    }, [communityLoading]);
    useEffect(() => {
        if (communityError) {
            setError(communityError === null || communityError === void 0 ? void 0 : communityError.message);
        }
    }, [communityError]);
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(PATHS.COMMUNITY_SETTINGS_DETAILS);
    const pageTitle = useMemo(() => {
        return (community && `${community === null || community === void 0 ? void 0 : community.name} - ${intl.formatMessage(messages.communitySettingsTitle)}`) || "";
    }, [community]);
    const isRegionalLeasingEnabled = useMemo(() => {
        return usePolicyContext("Regional Leasing" /* PolicyActions.REGIONAL_LEASING */);
    }, [proxyUser]);
    useEffect(() => {
        if (pageTitle) {
            pageCtx.setLayoutConfig({
                pageTitle,
                backButton: true,
                displaySearch: false,
                onBack: () => navigate(PATHS.SPACES),
            });
            analytics.page({
                title: `Peek Dashboard || ${intl.formatMessage(messages.communitySettingsTitle)}`,
            });
        }
    }, [pageTitle]);
    const handleTabChange = (value) => {
        navigate(value);
    };
    const COMMUNITY_SETTINGS_TABS = useMemo(() => {
        var _a, _b;
        return [
            {
                value: PATHS.COMMUNITY_SETTINGS_DETAILS,
                label: messages.communitySettingsDetailsTabTitle,
            },
            {
                value: PATHS.COMMUNITY_SETTINGS_WIDGETS,
                label: messages.communitySettingsWidgetsTabTitle,
            },
            ...((!!proxyUser &&
                ((_a = community === null || community === void 0 ? void 0 : community.features) === null || _a === void 0 ? void 0 : _a.some((feature) => CASA_FEATURES_SET.has(feature))) && [
                {
                    value: PATHS.COMMUNITY_SETTINGS_CASA,
                    label: messages.communitySettingsChatbotTabTitle,
                },
            ]) ||
                []),
            ...((!!((_b = community === null || community === void 0 ? void 0 : community.features) === null || _b === void 0 ? void 0 : _b.includes("AutoScan" /* PolicyActions.AUTOSCAN */)) && [
                {
                    value: PATHS.COMMUNITY_SETTINGS_AUTOSCAN,
                    label: messages.communitySettingsAutoscanTabTitle,
                },
            ]) ||
                []),
            ...((proxyUser && [
                {
                    value: PATHS.COMMUNITY_SETTINGS_DX,
                    label: messages.communitySettingsDxTabTitle,
                },
            ]) ||
                []),
            ...((!!isRegionalLeasingEnabled && [
                {
                    value: PATHS.COMMUNITY_SETTINGS_REGIONAL_LEASING,
                    label: messages.communitySettingsRegionalLeasingTabTitle,
                },
            ]) ||
                []),
        ];
    }, [community, proxyUser]);
    useEffect(() => {
        const usersURLSegments = location.pathname.split("/");
        const activeTabURL = usersURLSegments.pop() || usersURLSegments.pop();
        const isValidTabValue = COMMUNITY_SETTINGS_TABS.map(({ value }) => value).includes(activeTabURL);
        setTabValue((isValidTabValue && activeTabURL) || PATHS.COMMUNITY_SETTINGS_DETAILS);
    }, [location.pathname, COMMUNITY_SETTINGS_TABS]);
    return (React.createElement(PeekPaper, { sx: { p: 2 } },
        React.createElement(RefetchProvider, null,
            React.createElement(TabsLayout, { tabs: COMMUNITY_SETTINGS_TABS, activeTab: tabValue, onTabChange: handleTabChange, TabAction: [], TabContent: React.createElement(PeekBox, { pt: theme.spacing(2) },
                    React.createElement(Outlet, { context: { community: community, getCommunity: getCommunity } })) }))));
};
